import React, { useState } from 'react';
import {
  Box, Paper, Typography, List, ListItemButton, ListItemIcon,
  ListItemText, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip, Divider,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ArticleIcon from '@mui/icons-material/Article';
import PrintIcon from '@mui/icons-material/Print';
import PhoneIcon from '@mui/icons-material/Phone';
import CodeIcon from '@mui/icons-material/Code';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import { Referral, ReferralDocument, ReferralSource, FlagSeverity } from '~/types/transition';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';

interface DocItem {
  icon: React.ReactNode;
  label: string;
  content: string;
  warning?: boolean;
  url?: string;
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function getIntakeDocuments(referral: Referral): DocItem[] {
  const date = formatDate(referral.receivedAt);
  const hasIllegibleFlag = referral.flags.some(
    (f) => f.severity === FlagSeverity.BLOCKING && f.label.toLowerCase().includes('illegible')
  );

  switch (referral.source) {
    case ReferralSource.EMAIL:
      return [
        {
          icon: <EmailIcon fontSize="small" />,
          label: `Referral Email — ${date}`,
          content: `From: ${referral.patient.referringProvider} <referrals@${referral.patient.referringFacility.toLowerCase().replace(/\s/g, '')}.org>\nTo: referrals@medarrive.com\nSubject: Home Health Referral — ${referral.patient.name}\n\nPlease find attached the referral documentation for ${referral.patient.name}, DOB ${referral.patient.dob}. The patient is being referred for ${referral.patient.requestedProgram} services following recent hospitalization.\n\nDiagnosis: ${referral.patient.diagnosisLabels.join(', ')}\nInsurance: ${referral.patient.insurance}\n\nPlease contact our office with any questions.\n\n${referral.patient.referringProvider}\n${referral.patient.referringFacility}`,
        },
        {
          icon: <AttachFileIcon fontSize="small" />,
          label: 'Attachment: referral_order.pdf',
          content: '[Simulated PDF attachment]\n\nReferral Order\nPatient: ' + referral.patient.name + '\nDate: ' + date + '\nDiagnosis: ' + referral.patient.diagnosisLabels.join(', ') + '\nRequested Service: ' + referral.patient.requestedProgram,
        },
      ];

    case ReferralSource.EFAX:
      return [
        {
          icon: <ArticleIcon fontSize="small" />,
          label: `E-Fax Document — ${date}`,
          content: `[Simulated E-Fax Document]\n\nReceived via electronic fax from ${referral.patient.referringFacility}.\n\nReferral for: ${referral.patient.name}\nDOB: ${referral.patient.dob}\nDiagnosis: ${referral.patient.diagnosisLabels.join(', ')}\nRequested Program: ${referral.patient.requestedProgram}\nInsurance: ${referral.patient.insurance} (${referral.patient.insuranceMemberId})\n\nReferring Provider: ${referral.patient.referringProvider}\nFacility: ${referral.patient.referringFacility}`,
        },
      ];

    case ReferralSource.FAX:
      if (referral.uploadedFileUrl) {
        return [
          {
            icon: <ArticleIcon fontSize="small" />,
            label: referral.uploadedFileName ?? 'Referral Document',
            content: '',
            url: referral.uploadedFileUrl,
          },
        ];
      }
      return [
        {
          icon: <PrintIcon fontSize="small" />,
          label: 'Fax Image — Page 1 of 3',
          content: `[Simulated Fax Image — Page 1 of 3]\n\nReferral Form\nPatient Name: ${referral.patient.name}\nDate of Birth: ${referral.patient.dob}\nAddress: ${referral.patient.address}, ${referral.patient.city}, ${referral.patient.state} ${referral.patient.zip}\n\nDiagnosis: ${referral.patient.diagnosisLabels.join(', ')}\nRequested Service: ${referral.patient.requestedProgram}`,
        },
        {
          icon: <PrintIcon fontSize="small" />,
          label: hasIllegibleFlag ? 'Fax Image — Page 2 of 3 (partially illegible)' : 'Fax Image — Page 2 of 3',
          content: hasIllegibleFlag
            ? '[Simulated Fax Image — Page 2 of 3]\n\n⚠️ PARTIALLY ILLEGIBLE — portions of this page could not be read.\n\nInsurance Information:\nCarrier: [ILLEGIBLE]\nMember ID: [ILLEGIBLE]\n\nReferring Provider:\nName: [ILLEGIBLE]\nPhone: [ILLEGIBLE]'
            : `[Simulated Fax Image — Page 2 of 3]\n\nInsurance Information:\nCarrier: ${referral.patient.insurance}\nMember ID: ${referral.patient.insuranceMemberId || '—'}\n\nReferring Provider:\nName: ${referral.patient.referringProvider}\nPhone: ${referral.patient.referringPhone || '—'}`,
          warning: hasIllegibleFlag,
        },
        {
          icon: <PrintIcon fontSize="small" />,
          label: 'Fax Image — Page 3 of 3',
          content: `[Simulated Fax Image — Page 3 of 3]\n\nPhysician Signature and Orders\n\nSigned: ${referral.patient.referringProvider}\nFacility: ${referral.patient.referringFacility}\nDate: ${date}`,
        },
      ];

    case ReferralSource.PHONE:
      return [
        {
          icon: <PhoneIcon fontSize="small" />,
          label: `Phone Intake Form — ${date} (manually entered)`,
          content: `[Phone Intake Form]\n\nCompleted by: Intake Coordinator\nDate/Time: ${date}\nMethod: Phone call from ${referral.patient.referringFacility}\n\nPatient: ${referral.patient.name}\nDOB: ${referral.patient.dob}\nAddress: ${referral.patient.address}, ${referral.patient.city}, ${referral.patient.state} ${referral.patient.zip}\nPhone: ${referral.patient.phone || '—'}\n\nInsurance: ${referral.patient.insurance}\nMember ID: ${referral.patient.insuranceMemberId || '—'}\n\nDiagnosis: ${referral.patient.diagnosisLabels.join(', ')}\nRequested Service: ${referral.patient.requestedProgram}\n\nReferring Provider: ${referral.patient.referringProvider}\nFacility: ${referral.patient.referringFacility}\nPhone: ${referral.patient.referringPhone || '—'}\n\nNote: All data manually entered. Recommend verification at start of care.`,
        },
      ];

    case ReferralSource.API:
      return [
        {
          icon: <CodeIcon fontSize="small" />,
          label: `API Payload — structured JSON (${date})`,
          content: JSON.stringify({
            source: 'API',
            receivedAt: referral.receivedAt,
            patient: {
              name: referral.patient.name,
              dob: referral.patient.dob,
              address: referral.patient.address,
              city: referral.patient.city,
              state: referral.patient.state,
              zip: referral.patient.zip,
              phone: referral.patient.phone,
              insurance: referral.patient.insurance,
              insuranceMemberId: referral.patient.insuranceMemberId,
            },
            clinical: {
              diagnosisCodes: referral.patient.diagnosisCodes,
              diagnosisLabels: referral.patient.diagnosisLabels,
              requestedProgram: referral.patient.requestedProgram,
            },
            referringProvider: {
              name: referral.patient.referringProvider,
              facility: referral.patient.referringFacility,
              phone: referral.patient.referringPhone,
              fax: referral.patient.referringFax,
            },
          }, null, 2),
        },
      ];

    default:
      return [];
  }
}

interface DocumentsCardProps {
  referral: Referral;
}

const MAX_ADDITIONAL_DOCS = 3;

const DocumentsCard: React.FC<DocumentsCardProps> = ({ referral }) => {
  const { simulateIncomingDocument } = useTransitionContext();
  const [selected, setSelected] = useState<{ label: string; content: string } | null>(null);

  const intakeDocs = getIntakeDocuments(referral);
  const additionalDocs: ReferralDocument[] = referral.additionalDocuments ?? [];
  const canSimulate = additionalDocs.length < MAX_ADDITIONAL_DOCS;

  return (
    <>
      {/* ── Intake Documents ────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Intake Documents
        </Typography>
        <List dense disablePadding>
          {intakeDocs.map((doc, i) => (
            <ListItemButton
              key={i}
              onClick={() => doc.url ? window.open(doc.url, '_blank', 'noopener,noreferrer') : setSelected({ label: doc.label, content: doc.content })}
              sx={{ borderRadius: 1, px: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: doc.warning ? 'warning.main' : 'text.secondary' }}>
                {doc.warning ? <WarningAmberIcon fontSize="small" color="warning" /> : doc.icon}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{doc.label}</Typography>
                    {doc.warning && (
                      <Chip label="Illegible" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 18 }} />
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      {/* ── Additional Documents ─────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Additional Documents
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Documents received after initial intake and matched to this referral
            </Typography>
          </Box>
          <Chip
            label={additionalDocs.length}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>

        {additionalDocs.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 1.5, fontStyle: 'italic' }}>
            No additional documents received yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {additionalDocs.map((doc) => (
              <React.Fragment key={doc.id}>
                <ListItemButton
                  onClick={() => setSelected({ label: `${doc.type} — ${doc.fileName}`, content: doc.simulatedContent })}
                  sx={{ borderRadius: 1, px: 1, alignItems: 'flex-start', py: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary', mt: 0.5 }}>
                    <ArticleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{doc.type}</Typography>
                        <Typography variant="caption" color="text.secondary">{doc.fileName}</Typography>
                        {doc.isNew && (
                          <Chip label="NEW" size="small" color="primary" sx={{ fontSize: '0.6rem', height: 16, fontWeight: 700 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            via {doc.source} · {formatDate(doc.receivedAt)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.disabled" sx={{ mr: 0.25 }}>
                            Matched on:
                          </Typography>
                          {doc.matchedOn.map((field) => (
                            <Chip
                              key={field}
                              label={field}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.6rem', height: 16 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItemButton>
                <Divider sx={{ my: 0.5 }} />
              </React.Fragment>
            ))}
          </List>
        )}

        {referral.scenarioId === 'post-surgical-wound' && (
          <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddToPhotosIcon />}
              onClick={() => simulateIncomingDocument(referral.id)}
              disabled={!canSimulate}
              sx={{ fontSize: '0.75rem' }}
            >
              {canSimulate ? 'Simulate: Incoming Document' : 'All simulated documents received'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* ── Viewer dialog ────────────────────────────────────────────────────── */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected?.label}</DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: 'grey.50',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {selected?.content}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentsCard;
